import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { X, Plus, Minus, ShoppingBag, Trash2, ArrowRight, Sparkles, Truck } from 'lucide-react';
import { categories, FREE_SHIPPING_THRESHOLD } from '../../data/mock';

const CartDrawer = () => {
  const {
    isCartOpen,
    closeCart,
    cartItems,
    cartTotal,
    updateQuantity,
    removeFromCart
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={closeCart}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col border-l border-gray-100"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-athletic-500/10 rounded-full flex items-center justify-center border border-athletic-500/30">
              <ShoppingBag className="w-5 h-5 text-athletic-500" />
            </div>
            <div>
              <h2 className="text-lg text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>Your Cart</h2>
              <p className="text-sm text-[#1a1a1a]/50">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-gray-50 rounded-full transition-colors text-[#1a1a1a]/60 hover:text-[#1a1a1a]"
            data-testid="cart-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-athletic-50 rounded-full flex items-center justify-center mb-5 border border-athletic-100">
                <Sparkles className="w-8 h-8 text-athletic-400" />
              </div>
              <h3 className="text-[#1a1a1a] text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>Your cart is empty</h3>
              <p className="text-[#1a1a1a]/50 text-sm mb-6">Discover skincare to start your ritual</p>
              <button
                onClick={closeCart}
                className="px-6 py-3 bg-athletic-500 hover:bg-athletic-600 text-white font-medium tracking-wide rounded-full transition-all"
                data-testid="cart-browse"
              >
                Browse Skincare
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map(item => {
                const itemCategory = categories.find(c => c.id === item.categoryId);
                return (
                  <div key={item.id} className="flex gap-4 p-4 bg-[#faf7f2] rounded-2xl border border-gray-100">
                    <div className="w-20 h-20 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-athletic-500 uppercase tracking-[0.18em] font-medium mb-1">
                        {itemCategory?.name || 'Skincare'}
                      </p>
                      <h4 className="text-[#1a1a1a] text-sm mb-1 line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                        ${item.price.toFixed(2)}
                      </p>

                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-gray-200 rounded-full overflow-hidden bg-white">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-2 hover:bg-gray-50 transition-colors text-[#1a1a1a]/60"
                            data-testid={`cart-decrement-${item.id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm font-medium text-[#1a1a1a]">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-2 hover:bg-gray-50 transition-colors text-[#1a1a1a]/60"
                            data-testid={`cart-increment-${item.id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-[#1a1a1a]/40 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          data-testid={`cart-remove-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-6 border-t border-gray-100 space-y-4">
            {cartTotal < FREE_SHIPPING_THRESHOLD && (
              <div className="bg-[#faf7f2] rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#1a1a1a]/60 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-athletic-500" />
                    ${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)} more for FREE shipping
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-athletic-500 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            {cartTotal >= FREE_SHIPPING_THRESHOLD && (
              <div className="flex items-center gap-2 px-4 py-3 bg-athletic-50 border border-athletic-200 rounded-xl">
                <Truck className="w-5 h-5 text-athletic-500" />
                <span className="text-athletic-700 text-sm font-medium">FREE shipping unlocked!</span>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <span className="text-[#1a1a1a]/60 font-medium">Subtotal</span>
              <span className="text-2xl text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                ${cartTotal.toFixed(2)}
              </span>
            </div>

            <Link
              to="/checkout"
              onClick={closeCart}
              data-testid="cart-checkout-btn"
              className="flex items-center justify-center gap-2 w-full py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-medium tracking-wide rounded-full transition-all"
            >
              Checkout
              <ArrowRight className="w-5 h-5" />
            </Link>

            <button
              onClick={closeCart}
              className="w-full text-center text-[#1a1a1a]/50 hover:text-[#1a1a1a] py-2 text-sm font-medium transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
